import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { todayIn } from "../../../shared/dates";
import type { Member, Task } from "../../../shared/models";
import { kidsFirst } from "../../../shared/people";
import { describeRecurrence } from "../../../shared/tasks";
import { useHousehold } from "../../app/useHousehold";
import { api } from "../../data/api";
import { useLedger, useMembers, useRewards, useTasks, useUpdateSettings } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { formatReward, parseReward } from "../../ui/reward";
import { Sheet } from "../../ui/Sheet";
import { relativeAge } from "../../ui/time";
import { TaskEditor } from "../../widgets/tasks/TasksWidget";

/** Chores list, currency, balances, and paying out or adjusting rewards. */
export function RewardsSection() {
  const { isAdmin, timeZone } = useHousehold();
  const rewards = useRewards();
  const members = useMembers().data ?? [];
  const tasks = useTasks().data ?? [];
  const update = useUpdateSettings();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Task | "new" | null>(null);
  const [paying, setPaying] = useState<Member | null>(null);
  const currency = rewards.data?.currency ?? "stars";
  const chores = tasks.filter((t) => t.kind === "chore");
  // Children, plus any adult who has a chore or a balance.
  const earnerIds = new Set([...chores.map((t) => t.assigneeId), ...(rewards.data?.balances ?? []).filter((b) => b.balance !== 0).map((b) => b.memberId)]);
  const earners = kidsFirst(members).filter((m) => m.isChild || earnerIds.has(m.id));
  const byId = new Map(members.map((m) => [m.id, m]));

  const setCurrency = (c: "stars" | "dollars") =>
    update.mutate({ rewardCurrency: c }, { onSuccess: () => qc.invalidateQueries({ queryKey: ["rewards"] }) });

  return (
    <div className="form">
      <h3>Chores & rewards</h3>
      <div className="field">
        <span>Kids earn</span>
        <div className="segmented">
          <button className={currency === "stars" ? "on" : ""} disabled={!isAdmin} onClick={() => setCurrency("stars")}>
            ★ Stars
          </button>
          <button className={currency === "dollars" ? "on" : ""} disabled={!isAdmin} onClick={() => setCurrency("dollars")}>
            $ Dollars
          </button>
        </div>
        <p className="muted small">Switching doesn’t convert existing balances. Pick one when you start.</p>
      </div>

      <h4>Balances</h4>
      {earners.length === 0 ? (
        <p className="muted">Mark family members as children (Family section) to give them chores and balances.</p>
      ) : (
        <ul className="member-list">
          {earners.map((m) => {
            const b = rewards.data?.balances.find((x) => x.memberId === m.id);
            return (
              <li key={m.id}>
                <Avatar member={m} size="2.2rem" />
                <span>
                  <strong>{m.displayName}</strong>
                  <span className="muted small">
                    {formatReward(b?.balance ?? 0, currency)} · {formatReward(b?.earnedThisWeek ?? 0, currency)} earned this week
                  </span>
                </span>
                {isAdmin && (
                  <button className="btn btn-sm" onClick={() => setPaying(m)}>
                    Pay out / adjust
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h4>Chores</h4>
      {chores.length === 0 ? (
        <p className="muted">No chores yet. Add some, or tap + on the chore chart.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Chore</th>
              <th>Whose</th>
              <th>Repeats</th>
              <th>Reward</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {chores.map((t) => (
              <tr key={t.id}>
                <td>{t.title}</td>
                <td>{t.assigneeId ? byId.get(t.assigneeId)?.displayName : "Anyone"}</td>
                <td>{describeRecurrence(t.recurrence) ?? "Once"}</td>
                <td>{formatReward(t.reward, currency)}</td>
                <td>
                  <button className="btn btn-sm" onClick={() => setEditing(t)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <button className="btn" onClick={() => setEditing("new")}>
        <Icon name="plus" size="1.1rem" /> Add chore
      </button>

      <h4>Recent activity</h4>
      <LedgerList byId={byId} currency={currency} />

      {editing && <TaskEditor task={editing === "new" ? null : editing} kind="chore" defaultRepeat="daily" members={members} today={todayIn(timeZone)} onClose={() => setEditing(null)} />}
      {paying && <PayOut member={paying} currency={currency} balance={rewards.data?.balances.find((b) => b.memberId === paying.id)?.balance ?? 0} onClose={() => setPaying(null)} />}
    </div>
  );
}

function LedgerList({ byId, currency }: { byId: Map<string, Member>; currency: "stars" | "dollars" }) {
  const ledger = useLedger(null);
  const rows = ledger.data ?? [];
  if (rows.length === 0) return <p className="muted">Nothing yet.</p>;
  return (
    <table className="table">
      <tbody>
        {rows.slice(0, 25).map((r) => (
          <tr key={r.id}>
            <td>{byId.get(r.memberId)?.displayName ?? "—"}</td>
            <td>{r.kind === "spend" ? "Paid out / spent" : r.kind === "adjust" ? "Adjusted" : r.note}</td>
            <td className={r.amount < 0 ? "warn-text" : ""}>{formatReward(r.amount, currency, { sign: true })}</td>
            <td className="muted">{relativeAge(r.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayOut({ member, currency, balance, onClose }: { member: Member; currency: "stars" | "dollars"; balance: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"spend" | "adjust">("spend");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const v = parseReward(amount, currency);
    if (!v) return setError("Enter an amount");
    try {
      await api(`/family/rewards/${mode}`, { method: "POST", body: { memberId: member.id, amount: v, note } });
      await Promise.all([qc.invalidateQueries({ queryKey: ["rewards"] }), qc.invalidateQueries({ queryKey: ["ledger"] })]);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Sheet
      title={`${member.displayName}: ${formatReward(balance, currency)}`}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit}>
            {mode === "spend" ? "Pay out" : "Add"}
          </button>
        </>
      }
    >
      <div className="form">
        <div className="segmented">
          <button className={mode === "spend" ? "on" : ""} onClick={() => setMode("spend")}>
            Pay out / spend
          </button>
          <button className={mode === "adjust" ? "on" : ""} onClick={() => setMode("adjust")}>
            Bonus
          </button>
        </div>
        <label className="field">
          <span>Amount ({currency === "dollars" ? "$" : "stars"})</span>
          <input className="input input-lg" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={currency === "dollars" ? "5.00" : "10"} autoFocus />
        </label>
        <label className="field">
          <span>Note (optional)</span>
          <input className="input" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} placeholder={mode === "spend" ? "e.g. Movie night pick" : "e.g. Helped with groceries"} />
        </label>
        {error && <p className="error-text">{error}</p>}
      </div>
    </Sheet>
  );
}
